import { Box, Tab, Tabs } from '@mui/material';
import React, { useMemo, useRef } from 'react';
import { Redirect } from 'react-router-dom';
import { PageContainer, PageHeader } from 'app/components/StyledComponents';
import { BreadcrumbsComponent } from 'app/components/BreadcrumbsComponent';
import PleaseSelectDealerPlaceholder from 'app/components/ReusableUIComponents/LowLevelComponents/PleaseSelectDealerPlaceholder';
import FullWidthToggleMAIQ from 'app/components/ReusableUIComponents/LowLevelComponents/FullWidthToggleMAIQ';
import { NotFoundPage } from 'app/components/NotFoundPage';
import { useMetricsFrameworkPage } from '../hooks/useMetricsFrameworkPage';
import { METRICS_FRAMEWORK_VIEW_CONFIG, buildMetricsFrameworkPath } from '../utils/viewConfig';
import {
  METRICS_FRAMEWORK_PROTECTED_TAB_VIEWS,
  METRICS_FRAMEWORK_PROTECTED_TAB_VIEWS_BY_BASE_PATH,
} from '../utils/protectedTabViews';
import MetricsFrameworkHeaderControls from './MetricsFrameworkHeaderControls';
import MetricL2Modal from './MetricL2Modal';
import SectionL2Modal from './SectionL2Modal';
import { userProfile } from 'utils/auth';

const CONTENT_MAX_WIDTH = 1200;

export default function MetricsFrameworkPageContent() {
  const {
    basePath,
    allowedViews,
    selectedView,
    isViewValid,
    isViewAllowed,
    requestedView,
    breadcrumbsList,
    selectedTabIndex,
    handleTabChange,
    hasDealer,
    timeperiod,
    selectedTimeperiod,
    handleClickChangeTimeperiod,
    timePeriodRenderComponent,
    pageTitle,
    mockApiFlag,
    handleToggleMockData,
    canUseMockApiToggle,
  } = useMetricsFrameworkPage();

  const tabsContainerRef = useRef<HTMLDivElement | null>(null);
  const contentContainerRef = useRef<HTMLDivElement | null>(null);
  const fullWidthTargetRefs = useMemo(
    () => [tabsContainerRef, contentContainerRef],
    [],
  );

  const protectedTabViews =
    METRICS_FRAMEWORK_PROTECTED_TAB_VIEWS_BY_BASE_PATH[basePath] ??
    METRICS_FRAMEWORK_PROTECTED_TAB_VIEWS;

  const tabsData = useMemo(
    () =>
      allowedViews.map(view => {
        const ProtectedTabView = protectedTabViews[view];
        return {
          label: METRICS_FRAMEWORK_VIEW_CONFIG[view].tabLabel.toUpperCase(),
          content: ProtectedTabView ? <ProtectedTabView /> : null,
        };
      }),
    [allowedViews, protectedTabViews],
  );

  if (allowedViews.length === 0) {
    return <NotFoundPage />;
  }

  if (!requestedView || !isViewValid || !isViewAllowed) {
    return <Redirect to={buildMetricsFrameworkPath(basePath, allowedViews[0])} />;
  }

  const safeTabIndex = Math.max(selectedTabIndex, 0);
  const currentTab = tabsData[safeTabIndex];
  const user = userProfile(false);
  const stickyTop = user?.old_layout === false ? 0 : 64;

  return (
    <PageContainer sx={{ pb: 4 }}>
      <Box
        sx={{
          position: 'sticky',
          top: stickyTop,
          zIndex: 10,
          bgcolor: 'background.paper',
          mx: '-40px',
          mt: '-40px',
          px: '40px',
          pt: 3,
          pb: 0,
          boxShadow: '0 8px 24px 0 rgba(0,0,0,0.18)',
        }}
      >
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 3,
            flexWrap: 'wrap',
            pb: 2,
            mb: 0,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              gap: 0.75,
              minWidth: 0,
              flex: '1 1 auto',
            }}
          >
            <BreadcrumbsComponent breadcrumbsList={breadcrumbsList} />
            <PageHeader sx={{ marginBottom: '0 !important' }}>{pageTitle}</PageHeader>
          </Box>
          <MetricsFrameworkHeaderControls
            timeperiod={timeperiod}
            selectedTimeperiod={selectedTimeperiod}
            handleClickChangeTimeperiod={handleClickChangeTimeperiod}
            renderComponent={timePeriodRenderComponent}
            disabled={!hasDealer || !selectedView}
            mockApiFlag={mockApiFlag}
            handleToggleMockData={handleToggleMockData}
            showMockApiToggle={canUseMockApiToggle}
          />
        </Box>
        {hasDealer && tabsData.length > 0 ? (
          <Box sx={{ position: 'relative', width: '100%' }}>
            <Box
              ref={tabsContainerRef}
              sx={{
                maxWidth: CONTENT_MAX_WIDTH,
                width: '100%',
                mx: 'auto',
                boxSizing: 'border-box',
                pr: 6,
              }}
            >
              <Tabs
                value={safeTabIndex}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                }}
              >
                {tabsData.map(tab => (
                  <Tab key={tab.label} label={tab.label} />
                ))}
              </Tabs>
            </Box>
            <FullWidthToggleMAIQ
              targetRefs={fullWidthTargetRefs}
              constrainedMaxWidth={CONTENT_MAX_WIDTH}
              position={{ top: '50%', right: 0 }}
              tooltipExpand="Expand to full width"
              tooltipCollapse="Collapse to normal width"
              sx={{
                transform: 'translateY(-50%)',
              }}
            />
          </Box>
        ) : null}
      </Box>
      {!hasDealer ? (
        <PleaseSelectDealerPlaceholder />
      ) : (
        <Box
          ref={contentContainerRef}
          sx={{
            maxWidth: CONTENT_MAX_WIDTH,
            width: '100%',
            mx: 'auto',
            boxSizing: 'border-box',
            py: 3,
          }}
        >
          {currentTab?.content ?? null}
        </Box>
      )}
      <MetricL2Modal />
      <SectionL2Modal />
    </PageContainer>
  );
}
